import { useToastStore, type ToastAction } from '@/stores/toast.store';

/**
 * Toast Notification API
 *
 * Simple, global API for showing toast notifications
 * Uses Zustand store for state management
 *
 * @example
 * // Success
 * toast.success('Post created successfully!');
 *
 * @example
 * // Error with custom duration
 * toast.error('Failed to save post', { duration: 10000 });
 *
 * @example
 * // Warning
 * toast.warning('Some channels are offline');
 *
 * @example
 * // Info
 * toast.info('New feature available!');
 */

/**
 * Toast options
 */
interface ToastOptions {
  /**
   * Auto-dismiss duration in milliseconds
   * @default 5000 (success, warning, info), 7000 (error)
   */
  duration?: number;

  /** Optional inline action button (e.g. Undo) */
  action?: ToastAction;
}

/**
 * Show a success toast
 * Default duration: 5 seconds
 */
const success = (message: string, options?: ToastOptions) => {
  useToastStore.getState().addToast({
    type: 'success',
    message,
    duration: options?.duration || 5000,
    action: options?.action,
  });
};

/**
 * Show an error toast
 * Default duration: 7 seconds (longer for errors)
 */
const error = (message: string, options?: ToastOptions) => {
  useToastStore.getState().addToast({
    type: 'error',
    message,
    duration: options?.duration || 7000,
    action: options?.action,
  });
};

/**
 * Show a warning toast
 * Default duration: 5 seconds
 */
const warning = (message: string, options?: ToastOptions) => {
  useToastStore.getState().addToast({
    type: 'warning',
    message,
    duration: options?.duration || 5000,
    action: options?.action,
  });
};

/**
 * Show an info toast
 * Default duration: 5 seconds
 */
const info = (message: string, options?: ToastOptions) => {
  useToastStore.getState().addToast({
    type: 'info',
    message,
    duration: options?.duration || 5000,
    action: options?.action,
  });
};

/**
 * Toast API
 */
export const toast = {
  success,
  error,
  warning,
  info,
};
